import {formatDate} from '@angular/common';
import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import * as echarts from 'echarts';
import {adbAlpha, adbChartTheme, adbRedrawOnThemeChange, adbTooltip} from '@features/admin-panel/helpers/admin-dashboard-chart-theme';
import {adbUsdFull} from '@features/admin-panel/helpers/admin-dashboard-format';
import {AdminDashboardPoint} from '@features/admin-panel/models/admin-dashboard.models';

/**
 * The ECharts entry point behind a plain object, so a spec can stub it: the `echarts` module namespace is
 * read-only, and `spyOn(echarts, 'init')` throws.
 */
export const spendBarsCharts = {
  init: (element: HTMLElement): echarts.ECharts => echarts.init(element)
};

/**
 * The spend forecast card's daily XatBot spend (OCA-E-159 charts.ts spendBarsOption; ITP-E-163-05): one bar per day,
 * oldest first, in the light blue at 70%, the last bar red when a company spiked on that day, a dashed muted line at
 * the 14-day average, both axes hidden, 56 px, edge to edge, and a tooltip with the day, the spend and the average.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-spend-bars',
  templateUrl: './admin-dashboard-spend-bars.component.html',
  styleUrls: ['./admin-dashboard-spend-bars.component.scss']
})
export class AdminDashboardSpendBarsComponent implements AfterViewInit, OnChanges, OnDestroy {
  /** Redraws with the new theme's colours when the painted theme flips (GEN-E-137-08). */
  private readonly themeRedraw = adbRedrawOnThemeChange(() => this.render());
  /** One point per day, oldest first (the API's `Forecast.series`). */
  @Input() series: AdminDashboardPoint[] = [];
  /** USD per day: the dashed line (the API's `Forecast.avg14`). */
  @Input() avg14 = 0;
  /** The last bar is red (the API's `Forecast.spikeToday`). */
  @Input() spikeToday = false;

  @ViewChild('chart', {static: true}) chartContainer: ElementRef<HTMLDivElement>;

  private chart: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver;

  ngAfterViewInit(): void {
    this.chart = spendBarsCharts.init(this.chartContainer.nativeElement);
    this.resizeObserver = new ResizeObserver(() => this.chart?.resize());
    this.resizeObserver.observe(this.chartContainer.nativeElement);
    this.render();
  }

  ngOnChanges(): void {
    this.render();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.chart?.dispose();
    this.chart = null;
  }

  /** The chart as a PNG for the PDF report's 'chart' block (ITP-E-146); null before the chart exists or after it is gone. */
  reportImage(): {image: string; widthPx: number; heightPx: number} | null {
    if (!this.chart) {
      return null;
    }
    return {
      image: this.chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#FFFFFF'}),
      widthPx: this.chart.getWidth(),
      heightPx: this.chart.getHeight()
    };
  }

  private render(): void {
    if (!this.chart) {
      return;
    }
    const points = this.series ?? [];
    const values = points.map(point => point.value);
    const labels = points.map(point => point.date ? formatDate(point.date, 'd MMM', 'en-US', 'UTC') : '');
    const theme = adbChartTheme(this.chartContainer.nativeElement);
    const last = values.length - 1;
    const avg = this.avg14 ?? 0;
    const option: echarts.EChartsOption = {
      animation: false,
      grid: {left: 0, right: 0, top: 4, bottom: 0},
      xAxis: {type: 'category', show: false, data: labels},
      yAxis: {type: 'value', show: false},
      series: [{
        type: 'bar',
        barCategoryGap: '25%',
        data: values.map((value, i) => ({
          value,
          itemStyle: {color: this.spikeToday && i === last ? theme.red : adbAlpha(theme.blue2, 0.7)}
        })),
        markLine: {
          silent: true,
          symbol: 'none',
          label: {show: false},
          lineStyle: {color: theme.ink2, type: 'dashed', width: 1},
          data: [{yAxis: avg}]
        }
      }],
      // Appended to the body: the attention card would clip it.
      tooltip: adbTooltip(theme, {
        appendTo: 'body',
        formatter: (params: unknown): string => {
          const [first] = (Array.isArray(params) ? params : [params]) as {dataIndex?: number}[];
          const i = first?.dataIndex ?? 0;
          return `<b>${labels[i] ?? ''}</b><br/>${adbUsdFull(values[i] ?? 0)} XatBot spend`
            + `<br/><span style="color:${theme.ink2}">14-day average ${adbUsdFull(avg)}/day</span>`;
        }
      })
    };
    this.chart.setOption(option, true);
  }
}
